import type { AuctionData } from "../../../core/AdEnginePlugin";
import type { PrebidAdapterConfig } from "./Adapter";

/**
 * Abstract class for Prebid bidder adapters. Bidder adapters are responsible
 * for informing the PrebidPlugin if a bidder is enabled based on the ICBM
 * configuration.  It will also be able to transform ad unit bids for the
 * specific bidder before they are sent to Prebid.js if required using
 * `transformAdUnitBidConfigs` method.
 */
export class PrebidBidderAdapter<Bidder extends PrebidBidderName> {
  /**
   * The configuration flags that this adapter will check to see if it is enabled.
   */
  private readonly configFlags: (keyof PrebidAdapterConfig)[];

  /**
   * Creates a new PrebidAdapter instance configured with the provided configuration flags.
   * @param configFlags The configuration flags that this adapter will check to see if it is enabled.
   */
  constructor(...configFlags: (keyof PrebidAdapterConfig)[]) {
    this.configFlags = configFlags;
  }

  /**
   * Returns if the adapter should be considered enabled.
   *
   * @param config The ICBM configuration object.
   * @returns True if the adapter is enabled based on the provided configuration flags,
   * false otherwise. If not overridden by a subclass, it will return true only if all flags
   * passed into the constructor evaluate to true in the provided configuration.
   */
  isEnabled(config: PrebidAdapterConfig): boolean {
    if (this.configFlags.length === 0) {
      return true; // No flags means always enabled
    }

    return this.configFlags.every((flag) => !!config[flag]);
  }

  transformAdUnitBidConfigs(
    bidderConfigs: PrebidAdUnitBidForKey[Bidder][],
    auctionData: AuctionData,
    config: PrebidAdapterConfig,
  ): PrebidAdUnitBidForKey[Bidder][];

  /**
   * Called on every auction as PrebidPlugin creates the adUnits for that auction, this
   * allows the adapter to modify the `adUnit.bids` objects for the specific bidder if
   * required.
   *
   * @param bidderConfigs The PrebidAdUnitBid configuration for the specific bidder
   * this adapter was initialized for. This is the `PrebidAdUnit.bids` array.
   * @param auctionData The auction data for the current auction.
   * @param config The ICBM configuration object.
   */
  transformAdUnitBidConfigs(
    bidderConfigs: PrebidAdUnitBidForKey[Bidder][],
  ): PrebidAdUnitBidForKey[Bidder][] {
    // Default implementation does not transform the bids, just returns them as is.
    return bidderConfigs;
  }
}
